import { useTranslation } from "react-i18next"
import { ConnectionHandler, useFragment } from "react-relay"
import { useLocation, useOutletContext, useSearchParams } from "react-router"
import { PencilSimple, TrashSimple } from "@phosphor-icons/react"
import { useUser } from "@/apis/bff"
import { Button } from "@/components/ui/button"
import { toast } from "@/hooks/useToast"
import { scrollIntoField } from "@/lib/scrollToId"
import { getEpisodeOfCareFragment } from "@/modules/patients/detail/episode-of-care/lib/get-episode-of-care-fragment"
import { EpisodeOfCareFragment$key } from "@/modules/patients/detail/episode-of-care/types/episode-of-care-fragment-type"
import { useEditMode } from "@/modules/patients/detail/hooks/useEditMode"
import { DummyVideoCallButton } from "@/modules/patients/detail/progress-note/components/dummy-call/dummy-video-call-button"
import { PartnerVideoCallButton } from "@/modules/patients/detail/progress-note/components/partner-video-call-button"
import { VideoCallButton } from "@/modules/patients/detail/progress-note/components/video-call-button"
import { useIsProgressNoteEditable } from "@/modules/patients/detail/progress-note/hooks/useIsProgressNoteEditable"
import { useProgressNoteFormContext } from "@/modules/patients/detail/progress-note/hooks/useProgressNoteFormStore"
import { isDateOlderThan24Hours } from "@/modules/patients/detail/progress-note/lib/is-date-older-than-24-hours"
import { EpisodeOfCareProgressNoteData } from "@/modules/patients/detail/progress-note/types/progress-note-fragment-type"
import { useConfirmDialogStoreContext } from "@/modules/patients/detail/store/useConfirmDialogStore"
import { useAppointmentParam, useIsInContextOfAppointment } from "@/modules/patients/hooks/useAppointmentParam"
import { useCountryParam } from "@/modules/patients/hooks/useCountryParam"
import { usePatientParam } from "@/modules/patients/hooks/usePatientParam"
import { PatientFileLockFragment$key } from "@/modules/patients/services/__generated__/PatientFileLockFragment.graphql"
import { PatientFragment$data } from "@/modules/patients/services/__generated__/PatientFragment.graphql"
import { PatientFileLockFragment } from "@/modules/patients/services/PatientFileLockFragment"
import { useDeleteAppointmentMutation } from "@/modules/patients/services/useDeleteAppointmentMutation"
import { useDeleteProgressNoteMutation } from "@/modules/patients/services/useDeleteProgressNoteMutation"
import { useForceDeleteProgressNoteMutation } from "@/modules/patients/services/useForceDeleteProgressNoteMutation"
import { usePatientByIdQuery } from "@/modules/patients/services/usePatientByIdQuery"
import { useRequestTakeOverPatientMutation } from "@/modules/patients/services/useRequestTakeOverPatientMutation"
import { OutBoundCallButton } from "@/modules/telephony/call-button/call-button"
import { getAppointmentPhoneNumber } from "@/modules/waiting-room/libs/appointment_helper"
import { SofiaEnvironment } from "@/sofia-environment"
import { Country } from "@/types/__generated__/union-types"

interface ProgressNoteActionsProps {
  isEpisodeOfCareUpdatable?: boolean
  parentEpisodeOfCareFragmentReference: EpisodeOfCareFragment$key
  episodeOfCareProgressNoteData: EpisodeOfCareProgressNoteData
  isEditWithin24HoursAfterCloseMode: boolean
  onEditWithin24HoursAfterCloseModeChange: (value: boolean) => void
}

export const ProgressNoteActions = ({
  isEpisodeOfCareUpdatable,
  parentEpisodeOfCareFragmentReference,
  episodeOfCareProgressNoteData,
  isEditWithin24HoursAfterCloseMode,
  onEditWithin24HoursAfterCloseModeChange,
}: ProgressNoteActionsProps) => {
  const { role } = useUser()
  const patientId = usePatientParam()
  const isEditMode = useEditMode()
  const country = useCountryParam()
  const appointmentId = useAppointmentParam()
  const episodeOfCare = useFragment(getEpisodeOfCareFragment(country), parentEpisodeOfCareFragmentReference)
  const isInContextOfAppointment = useIsInContextOfAppointment()
  const isProgressNoteEditable = useIsProgressNoteEditable(episodeOfCareProgressNoteData)
  const deleteProgressNoteMutation = useDeleteProgressNoteMutation(country)
  const disableDeleteConfirmationDialog = useConfirmDialogStoreContext((state) => state.closeDeleteConfirmationDialog)
  const deleteProgressNoteForm = useProgressNoteFormContext((state) => state.deleteProgressNoteForm)
  const deleteAppointmentMutation = useDeleteAppointmentMutation()
  const setDeleteConfirmationDialogParams = useConfirmDialogStoreContext(
    (state) => state.setDeleteConfirmationDialogParams,
  )
  const { t } = useTranslation()

  const [patientData] = usePatientByIdQuery(patientId, country, appointmentId)
  if (!patientData.patientById) {
    throw new Error("Patient not found")
  }

  const { phoneNumber: patientPhoneNumber } = patientData.patientById

  const {
    id: progressNoteId,
    appointment: { status, channel },
  } = episodeOfCareProgressNoteData

  const appointmentPhoneNumber = getAppointmentPhoneNumber(
    episodeOfCareProgressNoteData.appointment.patientPhoneNumber,
    patientPhoneNumber,
  )

  if (!isEditMode) return

  if (role === "CustomerServiceCenter" && !isInContextOfAppointment) {
    return (
      <DeleteProgressNoteForCSCButton
        country={country}
        progressNoteId={progressNoteId}
        episodeOfCareId={episodeOfCare.id}
      />
    )
  }

  if (status === "CLOSED") {
    return (
      <ProgressNoteEdit
        appointmentId={appointmentId}
        episodeOfCareProgressNoteData={episodeOfCareProgressNoteData}
        isEditWithin24HoursAfterCloseMode={isEditWithin24HoursAfterCloseMode}
        onEditWithin24HoursAfterCloseModeChange={onEditWithin24HoursAfterCloseModeChange}
      />
    )
  } else {
    if (!isProgressNoteEditable || !isEpisodeOfCareUpdatable) return

    const handleDelete = () => {
      deleteProgressNoteMutation({ id: progressNoteId }, episodeOfCare.id, () => {
        disableDeleteConfirmationDialog()
        deleteProgressNoteForm(progressNoteId)

        if (episodeOfCareProgressNoteData.followUpAppointment) {
          const connectionId = ConnectionHandler.getConnectionID(patientId, "patientIdConnection_appointments")
          deleteAppointmentMutation({ id: episodeOfCareProgressNoteData.followUpAppointment.id }, [connectionId])
        }
      })
    }
    return (
      <div className="flex gap-2">
        {channel === "PHONE" && appointmentPhoneNumber && (
          <OutBoundCallButton
            appointmentId={episodeOfCareProgressNoteData.appointment.id}
            phoneNumber={appointmentPhoneNumber}
            patientGlobalId={patientId}
            patient={patientData}
            progressNoteGlobalId={progressNoteId}
            progressNoteId={episodeOfCareProgressNoteData.progressNoteId}
            language={episodeOfCareProgressNoteData.appointment.language}
          />
        )}
        {SofiaEnvironment.AllowDebugFeatures && channel === "VIDEO" && (
          <DummyVideoCallButton
            progressNoteId={progressNoteId}
            language={episodeOfCareProgressNoteData.appointment.language}
          />
        )}
        {channel === "VIDEO" && (
          <>
            <PartnerVideoCallButton
              progressNoteId={progressNoteId}
              language={episodeOfCareProgressNoteData.appointment.language}
              access={episodeOfCareProgressNoteData.appointment.access}
              service={episodeOfCareProgressNoteData.appointment.service}
              isPartnerInVideoCall={episodeOfCareProgressNoteData.appointment.isPartnerInVideoCall}
            />
            <VideoCallButton
              progressNoteId={progressNoteId}
              language={episodeOfCareProgressNoteData.appointment.language}
              contactChannel={patientData.patientById.contactChannel}
            />
          </>
        )}
        <Button
          variant="circle"
          className="h-10 w-10 shrink-0 p-1"
          data-testid="delete-progress-note-btn"
          onClick={() => {
            setDeleteConfirmationDialogParams({
              title: t("episodeOfCare.deleteProgressNote.title"),
              handleDelete: handleDelete,
            })
          }}>
          <TrashSimple />
        </Button>
      </div>
    )
  }
}

interface ProgressNoteEditProps {
  episodeOfCareProgressNoteData: EpisodeOfCareProgressNoteData
  isEditWithin24HoursAfterCloseMode: boolean
  onEditWithin24HoursAfterCloseModeChange: (value: boolean) => void
  appointmentId: string | null
}
const ProgressNoteEdit = ({
  episodeOfCareProgressNoteData,
  isEditWithin24HoursAfterCloseMode,
  onEditWithin24HoursAfterCloseModeChange,
  appointmentId,
}: ProgressNoteEditProps) => {
  const { t } = useTranslation()

  const { userName } = useUser()
  const { patient } = useOutletContext<{ patient: PatientFragment$data }>()

  const patientFileLock = useFragment<PatientFileLockFragment$key>(PatientFileLockFragment, patient.patientFileLock)
  const { inContextOfAppointment } = patientFileLock

  const [searchParams] = useSearchParams()
  const location = useLocation()

  const [requestTakeOverPatient] = useRequestTakeOverPatientMutation()

  const {
    appointment: { id: progressNoteAppointmentId, completedAt, isInvoiced, conclusionStatus },
    createdBy,
    id: progressNoteId,
  } = episodeOfCareProgressNoteData

  if (isDateOlderThan24Hours(completedAt) || createdBy !== userName) return

  const onEdit = () => {
    if (conclusionStatus === "IN_CONCLUSION") {
      toast({
        description: t("episodeOfCare.progressNote.reConcludeWarning"),
      })
      return
    }

    if (!appointmentId) {
      const navigateToAppointment = () => {
        searchParams.set("appointmentId", progressNoteAppointmentId)
        window.location.href = `${location.pathname}?${searchParams.toString()}`
      }

      if (inContextOfAppointment?.id !== progressNoteAppointmentId) {
        requestTakeOverPatient(
          { patientId: patient.id, appointmentId: progressNoteAppointmentId },
          navigateToAppointment,
        )
      } else {
        navigateToAppointment()
      }
    } else {
      onEditWithin24HoursAfterCloseModeChange(true)
    }
  }

  return (
    !isEditWithin24HoursAfterCloseMode &&
    !isInvoiced &&
    (!appointmentId || appointmentId === progressNoteAppointmentId) && (
      <Button
        variant="circle"
        className="h-10 w-10 shrink-0 p-1"
        data-testid="edit-progress-note-btn"
        onClick={(event) => {
          event.stopPropagation()
          onEdit()
          scrollIntoField(progressNoteId)
        }}>
        <PencilSimple />
      </Button>
    )
  )
}

interface DeleteProgressNoteForCSCButtonProps {
  country: Country
  progressNoteId: string
  episodeOfCareId: string
}

const DeleteProgressNoteForCSCButton = ({
  country,
  progressNoteId,
  episodeOfCareId,
}: DeleteProgressNoteForCSCButtonProps) => {
  const { t } = useTranslation()
  const forceDeleteProgressNoteMutation = useForceDeleteProgressNoteMutation(country)
  const setDeleteConfirmationDialogParams = useConfirmDialogStoreContext(
    (state) => state.setDeleteConfirmationDialogParams,
  )
  const deleteProgressNoteForm = useProgressNoteFormContext((state) => state.deleteProgressNoteForm)
  const disableDeleteConfirmationDialog = useConfirmDialogStoreContext((state) => state.closeDeleteConfirmationDialog)

  return (
    <Button
      variant="circle"
      className="h-10 w-10 shrink-0 p-1"
      data-testid="delete-progress-note-btn"
      onClick={() => {
        setDeleteConfirmationDialogParams({
          title: t("episodeOfCare.deleteProgressNote.title"),
          handleDelete: () => {
            forceDeleteProgressNoteMutation({ id: progressNoteId }, episodeOfCareId, () => {
              disableDeleteConfirmationDialog()
              deleteProgressNoteForm(progressNoteId)
            })
          },
        })
      }}>
      <TrashSimple />
    </Button>
  )
}
